import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'

export class PasswordProgress extends UcoastEl {
	static htmlSelector = 'password-progress'

	private progressInterval?: number
	private valueEl: HTMLElement
	private startDate?: Date
	private endDate?: Date

	constructor() {
		super()
		this.valueEl = q.rs('.password-progress__value', this)
		this.startDate = this.parseDate(this.dataset.start)
		this.endDate = this.parseDate(this.dataset.end)
	}

	override connectedCallback() {
		super.connectedCallback()

		if (!this.startDate && this.endDate) {
			this.startDate = new Date()
		}

		if (!this.endDate) {
			this.updateProgress()
			return
		}

		window.requestAnimationFrame(() => {
			this.updateProgress()
		})

		this.progressInterval = window.setInterval(() => {
			this.updateProgress()
		}, 1000)
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.stopProgress()
	}

	private parseDate(dateString?: string): Date | undefined {
		if (!dateString) return undefined

		const trimmed = dateString.trim()

		if (!trimmed) return undefined

		let normalized = trimmed.replace(' ', 'T')
		const timezoneMatch = normalized.match(/([+-]\d{2})(\d{2})$/)

		if (timezoneMatch) {
			normalized = normalized.replace(/([+-]\d{2})(\d{2})$/, '$1:$2')
		}

		const hasTimezone = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(normalized)
		const parsedDate = new Date(hasTimezone ? normalized : `${normalized}Z`)

		if (!Number.isNaN(parsedDate.getTime())) {
			return parsedDate
		}

		console.error('Invalid password progress date:', dateString)
		return undefined
	}

	private stopProgress() {
		if (this.progressInterval) {
			window.clearInterval(this.progressInterval)
		}
	}

	private updateProgress() {
		if (!this.endDate) {
			this.setProgress(0)
			return
		}

		const now = Date.now()
		const endTime = this.endDate.getTime()
		const startTime = this.startDate?.getTime()

		if (!startTime) {
			this.setProgress(0)
			return
		}

		if (endTime <= startTime) {
			this.setProgress(now >= endTime ? 100 : 0)
			return
		}

		const progress = ((now - startTime) / (endTime - startTime)) * 100
		const clampedProgress = Math.min(100, Math.max(0, progress))
		this.setProgress(clampedProgress)

		if (clampedProgress >= 100) {
			this.stopProgress()
		}
	}

	private setProgress(progress: number) {
		const clampedProgress = Math.min(100, Math.max(0, progress))
		const roundedProgress = Math.round(clampedProgress)

		this.style.setProperty('--password-progress-percent', `${roundedProgress}`)
		this.valueEl.textContent = `${roundedProgress}%`
		this.setAttribute('aria-valuenow', `${roundedProgress}`)
		this.setAttribute('aria-valuetext', `${roundedProgress}%`)

		if (roundedProgress >= 100) {
			this.classList.add('is-complete')
			return
		}

		this.classList.remove('is-complete')
	}
}
